import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/company.ts'
import { BrandsSection } from '#app/routes/mocra+/_form+/company/brands-section.tsx'
import { EscalationSection } from '#app/routes/mocra+/_form+/company/escalation-section.tsx'
import { HeadquartersSection } from '#app/routes/mocra+/_form+/company/headquarters-section.tsx'
import { IdentitySection } from '#app/routes/mocra+/_form+/company/identity-section.tsx'
import { AddressEditOverlay } from '#app/routes/mocra+/_form+/components/addresses/edit.tsx'
import {
	AddressSchema,
	createEmptyAddress,
} from '#app/routes/mocra+/_form+/components/addresses/schema.ts'
import {
	createAddressDraft,
	isAddressDraftEmpty,
	sanitizeAddressDraft,
	type AddressDraft,
} from '#app/routes/mocra+/_form+/components/addresses/types.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	type SectionKey,
	SECTIONS,
} from '#app/routes/mocra+/_sections+/section-data.ts'

export const meta: Route.MetaFunction = () => [{ title: 'MoCRA — Company' }]

const CompanySchema = z.object({
	companyLegalName: z
		.string({
			required_error:
				'Enter the legal name of the entity responsible for MoCRA (Modernization of Cosmetics Regulation Act) filings.',
		})
		.min(
			1,
			'Enter the legal name of the entity responsible for MoCRA (Modernization of Cosmetics Regulation Act) filings.',
		),
	responsiblePersonLabelName: z
		.string({
			required_error:
				'Enter the responsible party name exactly as it will appear on labels.',
		})
		.min(
			1,
			'Enter the responsible party name exactly as it will appear on labels.',
		),
	brandNames: z
		.string({
			required_error:
				'List the brands or product lines this SOP package will cover.',
		})
		.min(1, 'List the brands or product lines this SOP package will cover.'),
	hqAddress: AddressSchema,
	complianceContactEmail: z
		.string({
			required_error: 'Provide an email inbox for compliance escalations.',
		})
		.email('Enter a valid email inbox for compliance escalations.'),
})

export type CompanyFormValues = z.infer<typeof CompanySchema>

const DEFAULT_VALUES: CompanyFormValues = {
	companyLegalName: '',
	responsiblePersonLabelName: '',
	brandNames: '',
	hqAddress: createEmptyAddress(),
	complianceContactEmail: '',
}

type TemplateLink = (typeof SECTIONS)['overview']['templates'][number]

function selectTemplates(
	sectionKeys: ReadonlyArray<SectionKey>,
): TemplateLink[] {
	const seen = new Set<string>()
	const templates: TemplateLink[] = []
	for (const key of sectionKeys) {
		for (const template of SECTIONS[key].templates) {
			if (seen.has(template.filename)) continue
			seen.add(template.filename)
			templates.push(template)
		}
	}
	return templates
}

function createContext({
	mocraSections,
	templateSections,
}: {
	mocraSections: ReadonlyArray<SectionKey>
	templateSections: ReadonlyArray<SectionKey>
}) {
	return {
		mocraSections,
		templates: selectTemplates(templateSections),
	}
}

const COMPANY_SECTION_CONTEXT = {
	identity: createContext({
		mocraSections: ['overview', '607', '609'],
		templateSections: ['607', '609'],
	}),
	brands: createContext({
		mocraSections: ['overview', '607', '608'],
		templateSections: ['607', '608'],
	}),
	headquarters: createContext({
		mocraSections: ['607', '609', '610'],
		templateSections: ['607', '610'],
	}),
	escalation: createContext({
		mocraSections: ['605', '610', '611'],
		templateSections: ['605', '610', '611'],
	}),
}

function normalizeHeadquartersAddress(value: unknown): AddressDraft {
	if (typeof value === 'string') {
		return sanitizeAddressDraft(createAddressDraft({ street: value }))
	}
	if (!value || typeof value !== 'object') {
		return sanitizeAddressDraft(createAddressDraft())
	}
	const record = value as Record<string, unknown>
	return sanitizeAddressDraft(
		createAddressDraft({
			street: typeof record.street === 'string' ? record.street : '',
			street2: typeof record.street2 === 'string' ? record.street2 : '',
			city: typeof record.city === 'string' ? record.city : '',
			state: typeof record.state === 'string' ? record.state : '',
			postalCode:
				typeof record.postalCode === 'string' ? record.postalCode : '',
			country:
				typeof record.country === 'string'
					? record.country
					: DEFAULT_VALUES.hqAddress.country,
		}),
	)
}

function normalizeCompanyDefaults(draft: unknown): CompanyFormValues | null {
	if (!draft || typeof draft !== 'object') return null
	const record = draft as Record<string, unknown>
	const candidate: CompanyFormValues = {
		companyLegalName:
			typeof record.companyLegalName === 'string'
				? record.companyLegalName.trim()
				: DEFAULT_VALUES.companyLegalName,
		responsiblePersonLabelName:
			typeof record.responsiblePersonLabelName === 'string'
				? record.responsiblePersonLabelName.trim()
				: DEFAULT_VALUES.responsiblePersonLabelName,
		brandNames:
			typeof record.brandNames === 'string'
				? record.brandNames.trim()
				: DEFAULT_VALUES.brandNames,
		hqAddress: normalizeHeadquartersAddress(record.hqAddress),
		complianceContactEmail:
			typeof record.complianceContactEmail === 'string'
				? record.complianceContactEmail.trim()
				: DEFAULT_VALUES.complianceContactEmail,
	}
	const parsed = CompanySchema.safeParse(candidate)
	if (!parsed.success) return null
	return parsed.data
}

function companyValuesEqual(
	left: CompanyFormValues,
	right: CompanyFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function collectHeadquartersErrors(
	getFieldErrors: (path: string) => string[],
): string[] {
	const fields = ['street', 'street2', 'city', 'state', 'postalCode', 'country']
	const errors = new Set<string>()
	for (const error of getFieldErrors('hqAddress')) {
		errors.add(error)
	}
	for (const field of fields) {
		for (const error of getFieldErrors(`hqAddress.${field}`)) {
			errors.add(error)
		}
	}
	return Array.from(errors)
}

const SECTION_KEY = 'company'
const STEP_ID = 'company'

export default function Company() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [editOverlayOpen, setEditOverlayOpen] = useState(false)
	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = useSectionForm({
		section: SECTION_KEY,
		schema: CompanySchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeCompanyDefaults(values)
		if (normalized && !companyValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !companyValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const companyLegalName = values.companyLegalName ?? ''
	const responsiblePersonLabelName = values.responsiblePersonLabelName ?? ''
	const brandNames = values.brandNames ?? ''
	const complianceContactEmail = values.complianceContactEmail ?? ''

	const headquartersValue = values.hqAddress ?? createEmptyAddress()
	const headquartersDraft = useMemo(
		() => sanitizeAddressDraft(createAddressDraft(headquartersValue)),
		[headquartersValue],
	)
	const hasHeadquartersAddress = !isAddressDraftEmpty(headquartersDraft)
	const headquartersErrors = useMemo(
		() => collectHeadquartersErrors(getFieldErrors),
		[getFieldErrors],
	)
	const {
		identity: identityContext,
		brands: brandsContext,
		headquarters: headquartersContext,
		escalation: escalationContext,
	} = COMPANY_SECTION_CONTEXT

	const handleHeadquartersSubmit = (draft: AddressDraft) => {
		const sanitized = sanitizeAddressDraft(draft)
		setFieldValue('hqAddress', sanitized)
		setEditOverlayOpen(false)
	}

	const handleHeadquartersClear = () => {
		const cleared = sanitizeAddressDraft(
			createAddressDraft(createEmptyAddress()),
		)
		setFieldValue('hqAddress', cleared)
		setEditOverlayOpen(false)
	}

	const companyLegalNameErrors = getFieldErrors('companyLegalName')
	const responsiblePersonLabelNameErrors = getFieldErrors(
		'responsiblePersonLabelName',
	)
	const brandNameErrors = getFieldErrors('brandNames')
	const complianceContactEmailErrors = getFieldErrors('complianceContactEmail')

	return (
		<Page
			title="Company"
			description="These details flow into facility registrations, product listings, and the cover pages of each SOP (standard operating procedure)."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<IdentitySection
					resources={identityContext}
					companyLegalName={companyLegalName}
					companyLegalNameErrors={companyLegalNameErrors}
					onCompanyLegalNameChange={(next) =>
						setFieldValue('companyLegalName', next)
					}
					responsiblePersonLabelName={responsiblePersonLabelName}
					responsiblePersonLabelNameErrors={responsiblePersonLabelNameErrors}
					onResponsiblePersonLabelNameChange={(next) =>
						setFieldValue('responsiblePersonLabelName', next)
					}
				/>
				<Divider />

				<BrandsSection
					resources={brandsContext}
					brandNames={brandNames}
					errors={brandNameErrors}
					onChange={(next) => setFieldValue('brandNames', next)}
				/>
				<Divider />

				<HeadquartersSection
					resources={headquartersContext}
					address={headquartersDraft}
					hasAddress={hasHeadquartersAddress}
					errors={headquartersErrors}
					onSubmit={handleHeadquartersSubmit}
					onEdit={() => setEditOverlayOpen(true)}
					onClear={handleHeadquartersClear}
				/>
				<AddressEditOverlay
					open={editOverlayOpen}
					onOpenChange={setEditOverlayOpen}
					onSubmit={handleHeadquartersSubmit}
					initialValue={headquartersDraft}
					onCancel={() => setEditOverlayOpen(false)}
					onClosed={() => setEditOverlayOpen(false)}
				/>
				<Divider />

				<EscalationSection
					resources={escalationContext}
					complianceContactEmail={complianceContactEmail}
					errors={complianceContactEmailErrors}
					onChange={(next) => setFieldValue('complianceContactEmail', next)}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}
